import React, { useState, useRef } from 'react';
import '../styles/home.css';
import Modal from '@cloudscape-design/components/modal';
import Box from '@cloudscape-design/components/box';
import SpaceBetween from '@cloudscape-design/components/space-between';
import Button from '@cloudscape-design/components/button';
import FormField from '@cloudscape-design/components/form-field';
import Spinner from '@cloudscape-design/components/spinner';
import { PDFDocument, PDFHeader } from '@cantoo/pdf-lib';
import { addUsage } from './add-usage.js';
import constants from '../constants.js';

const MIN_PASSWORD_LENGTH = 4;

const PasswordProtectPdf = ({ isNew }) => {
  const [visible, setVisible] = useState(false);
  const [file, setFile] = useState(null);
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [submitValue, setSubmitValue] = useState('Protect PDF');
  const [done, setDone] = useState(false);
  const [fileError, setFileError] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [confirmError, setConfirmError] = useState('');
  const fileInputRef = useRef(null);

  const resetState = () => {
    setFile(null);
    setPassword('');
    setConfirmPassword('');
    setSubmitValue('Protect PDF');
    setDone(false);
    setFileError('');
    setPasswordError('');
    setConfirmError('');
    if (fileInputRef.current) fileInputRef.current.value = null;
  };

  const toggleModal = () => {
    setVisible(!visible);
    resetState();
  };

  const handleFileChange = (event) => {
    const selectedFile = event.target.files[0];
    if (!selectedFile) {
      setFile(null);
      return;
    }
    if (selectedFile.type !== constants.pdfFileType) {
      setFile(null);
      setFileError(constants.onlyPdfAllowed);
      return;
    }
    setFile(selectedFile);
    setFileError('');
    setDone(false);
    setSubmitValue('Protect PDF');
  };

  const handlePasswordChange = (event) => {
    const value = event.target.value;
    setPassword(value);
    if (value && value.length < MIN_PASSWORD_LENGTH) {
      setPasswordError(`Password must be at least ${MIN_PASSWORD_LENGTH} characters`);
    } else {
      setPasswordError('');
    }
    if (confirmPassword && value !== confirmPassword) {
      setConfirmError('Passwords do not match');
    } else {
      setConfirmError('');
    }
  };

  const handleConfirmChange = (event) => {
    const value = event.target.value;
    setConfirmPassword(value);
    if (value && value !== password) {
      setConfirmError('Passwords do not match');
    } else {
      setConfirmError('');
    }
  };

  const isValid =
    file &&
    !fileError &&
    password.length >= MIN_PASSWORD_LENGTH &&
    password === confirmPassword;

  const protectPdf = async () => {
    if (!isValid) return;
    setSubmitValue(<Spinner />);
    try {
      const arrayBuffer = await file.arrayBuffer();
      const doc = await PDFDocument.load(arrayBuffer);

      // Force the PDF header to 1.7 so the encrypt() call selects the v4
      // security handler => AESV2 (AES 128-bit) encryption.
      doc.context.header = PDFHeader.forVersion(1, 7);

      doc.encrypt({
        userPassword: password, // password required to OPEN the document
        ownerPassword: password, // required by the library
        permissions: {
          printing: 'highResolution',
          modifying: false,
          copying: false,
          annotating: false,
        },
      });

      const encryptedBytes = await doc.save({ useObjectStreams: false });

      const blob = new Blob([encryptedBytes], { type: constants.pdfFileType });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `protected_${file.name.replace(/\.pdf$/i, '')}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);

      addUsage('AWESOME PDF TOOLS PasswordProtect', '1.0', 1, window.location.href);
      setSubmitValue('Success');
      setDone(true);
    } catch (err) {
      console.error('Password protect error:', err);
      setFileError('Failed to protect PDF. Please try again with a different file.');
      setSubmitValue('Protect PDF');
    }
  };

  return (
    <div>
      <div className="service-card" onClick={toggleModal}>
        {isNew && <span className="badge badge-new">NEW</span>}
        <div className="service-card-icon">
          <svg viewBox="0 0 24 24" fill="#FF9900" width="40" height="40" aria-hidden="true">
            <path d="M12 1a5 5 0 0 0-5 5v3H6a2 2 0 0 0-2 2v9a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-9a2 2 0 0 0-2-2h-1V6a5 5 0 0 0-5-5zm0 2a3 3 0 0 1 3 3v3H9V6a3 3 0 0 1 3-3zm0 11a2 2 0 0 1 1 3.73V20a1 1 0 0 1-2 0v-2.27A2 2 0 0 1 12 14z" />
          </svg>
        </div>
        <h3 className="service-card-title">Password Protect PDF</h3>
        <p className="service-card-desc">
          Encrypt your files using robust AES 128-bit encryption and add password protection
        </p>
        <div className="service-card-action">
          <span>Get Started</span>
          <svg viewBox="0 0 24 24" fill="currentColor">
            <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
          </svg>
        </div>
      </div>

      <Modal
        onDismiss={toggleModal}
        visible={visible}
        size="medium"
        footer={
          <Box float="right">
            <SpaceBetween direction="horizontal" size="xs">
              <Button variant="link" onClick={toggleModal}>
                Cancel
              </Button>
              {!done ? (
                <Button variant="primary" disabled={!isValid} onClick={protectPdf}>
                  {submitValue}
                </Button>
              ) : (
                <Button variant="primary" onClick={resetState}>
                  Protect Another PDF
                </Button>
              )}
            </SpaceBetween>
          </Box>
        }
        header="Password Protect PDF"
      >
        <div>
          {/* File Upload */}
          <div className="bordered-box">
            <h6 className="container-header">🔒 Choose PDF File</h6>
            <FormField label="Select the PDF you want to protect" errorText={fileError}>
              <input
                ref={fileInputRef}
                type="file"
                accept=".pdf,application/pdf"
                onChange={handleFileChange}
              />
            </FormField>
            {file && (
              <div style={{ marginTop: '8px', fontSize: '13px', color: '#666' }}>
                <strong>Selected:</strong> {file.name} ({(file.size / 1024 / 1024).toFixed(2)} MB)
              </div>
            )}
          </div>

          {/* Password */}
          {!done && (
            <div className="bordered-box">
              <h6 className="container-header">🔑 Set Password</h6>

              <FormField label="Password" errorText={passwordError}>
                <input
                  type="password"
                  value={password}
                  onChange={handlePasswordChange}
                  placeholder="Enter a password"
                  autoComplete="new-password"
                  style={{
                    width: '100%',
                    padding: '8px',
                    border: '1px solid #d1d1d1',
                    borderRadius: '4px',
                  }}
                />
              </FormField>

              <div style={{ marginTop: '12px' }}>
                <FormField label="Confirm Password" errorText={confirmError}>
                  <input
                    type="password"
                    value={confirmPassword}
                    onChange={handleConfirmChange}
                    placeholder="Re-enter the password"
                    autoComplete="new-password"
                    style={{
                      width: '100%',
                      padding: '8px',
                      border: '1px solid #d1d1d1',
                      borderRadius: '4px',
                    }}
                  />
                </FormField>
              </div>

              <div style={{ fontSize: '0.85em', color: '#666', marginTop: '12px' }}>
                Anyone opening this PDF will be required to enter the password. Everything happens
                locally in your browser — your file is never uploaded to a server.
              </div>
            </div>
          )}

          {/* Success */}
          {done && (
            <div className="bordered-box">
              <h6 className="container-header">✅ PDF Protected!</h6>
              <div
                style={{
                  backgroundColor: '#e6f7e6',
                  border: '1px solid #52c41a',
                  borderRadius: '8px',
                  padding: '12px',
                }}
              >
                <span style={{ color: '#135200' }}>
                  🔐 AES 128-bit encrypted • 🔒 100% Private • Download started automatically
                </span>
              </div>
              <div style={{ fontSize: '0.85em', color: '#666', marginTop: '12px' }}>
                Your protected file <strong>protected_{file ? file.name.replace(/\.pdf$/i, '') : ''}.pdf</strong>{' '}
                has been downloaded. You will need the password to open it.
              </div>
            </div>
          )}
        </div>
      </Modal>
    </div>
  );
};

export default PasswordProtectPdf;
